import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Plus, X, ArrowUp, ArrowDown } from "lucide-react";
import { getLegalPage, updateLegalPage, formatApiErrorDetail } from "@/admin/adminApi";

const FIELD_CLASS = "w-full mt-1.5 bg-[var(--bg-main)] border border-[var(--border-default)] px-3 py-2 text-white focus:border-[var(--brand-primary)] outline-none";
const LABEL_CLASS = "text-xs font-mono uppercase tracking-widest text-white/60";

export default function AdminLegalPageEditPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getLegalPage(slug).then((data) => {
      setTitle(data.title);
      setSections(data.sections);
      setLoading(false);
    });
  }, [slug]);

  const addSection = () => setSections((s) => [...s, { heading: "", body: "", bullets: [] }]);
  const removeSection = (si) => setSections((s) => s.filter((_, i) => i !== si));
  const updateSection = (si, key, value) => setSections((s) => s.map((sec, i) => (i === si ? { ...sec, [key]: value } : sec)));
  const moveSection = (si, dir) => setSections((s) => {
    const newIndex = si + dir;
    if (newIndex < 0 || newIndex >= s.length) return s;
    const copy = [...s];
    [copy[si], copy[newIndex]] = [copy[newIndex], copy[si]];
    return copy;
  });
  const addBullet = (si) => setSections((s) => s.map((sec, i) => (i === si ? { ...sec, bullets: [...sec.bullets, ""] } : sec)));
  const removeBullet = (si, bi) => setSections((s) => s.map((sec, i) => (i === si ? { ...sec, bullets: sec.bullets.filter((_, j) => j !== bi) } : sec)));
  const updateBullet = (si, bi, value) => setSections((s) => s.map((sec, i) => (i === si ? { ...sec, bullets: sec.bullets.map((b, j) => (j === bi ? value : b)) } : sec)));

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await updateLegalPage(slug, title, sections);
      navigate("/admin/legal-pages");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-white/60 font-mono text-sm">Loading…</p>;

  return (
    <div data-testid="admin-legal-page-edit" className="max-w-3xl">
      <Link to="/admin/legal-pages" data-testid="admin-legal-edit-back-link" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-6">
        <ArrowLeft size={14} /> Back to Policy Pages
      </Link>
      <h1 className="font-display text-3xl text-white mb-6">Edit — {title}</h1>

      <form onSubmit={handleSave} className="space-y-8">
        <div>
          <label className={LABEL_CLASS}>Page Title</label>
          <input data-testid="admin-legal-title" value={title} onChange={(e) => setTitle(e.target.value)} className={FIELD_CLASS} />
        </div>

        <div>
          <span className={LABEL_CLASS}>Sections</span>
          <p className="text-white/40 text-xs mt-1 mb-3">
            Each section gets a heading, a paragraph, and an optional bullet list. Rendered in this exact order on the public page.
          </p>
          <div className="space-y-4">
            {sections.map((section, si) => (
              <div key={si} data-testid={`admin-legal-section-${si}`} className="border border-[var(--border-default)] bg-[var(--bg-secondary)]/40 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    data-testid={`admin-legal-section-heading-${si}`}
                    value={section.heading}
                    onChange={(e) => updateSection(si, "heading", e.target.value)}
                    className={FIELD_CLASS + " mt-0 flex-1"}
                    placeholder="Section heading, e.g. How We Use Your Information"
                  />
                  <button type="button" onClick={() => moveSection(si, -1)} disabled={si === 0} data-testid={`admin-legal-section-up-${si}`} className="text-white/50 hover:text-[var(--brand-primary)] disabled:opacity-30 shrink-0">
                    <ArrowUp size={16} />
                  </button>
                  <button type="button" onClick={() => moveSection(si, 1)} disabled={si === sections.length - 1} data-testid={`admin-legal-section-down-${si}`} className="text-white/50 hover:text-[var(--brand-primary)] disabled:opacity-30 shrink-0">
                    <ArrowDown size={16} />
                  </button>
                  <button type="button" onClick={() => removeSection(si)} data-testid={`admin-legal-section-remove-${si}`} className="text-white/50 hover:text-red-400 shrink-0">
                    <X size={16} />
                  </button>
                </div>
                <textarea
                  data-testid={`admin-legal-section-body-${si}`}
                  rows={4}
                  value={section.body}
                  onChange={(e) => updateSection(si, "body", e.target.value)}
                  className={FIELD_CLASS + " mt-0"}
                  placeholder="Paragraph copy for this section…"
                />
                <div className="space-y-2 pl-2 border-l border-[var(--border-default)]">
                  {section.bullets.map((b, bi) => (
                    <div key={bi} data-testid={`admin-legal-section-bullet-${si}-${bi}`} className="flex items-center gap-2 pl-3">
                      <input
                        value={b}
                        onChange={(e) => updateBullet(si, bi, e.target.value)}
                        className={FIELD_CLASS + " mt-0 flex-1"}
                        placeholder="Bullet point"
                      />
                      <button type="button" onClick={() => removeBullet(si, bi)} data-testid={`admin-legal-section-bullet-remove-${si}-${bi}`} className="text-white/50 hover:text-red-400 shrink-0">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addBullet(si)} data-testid={`admin-legal-section-bullet-add-${si}`} className="pl-3 text-xs font-mono text-white/50 hover:text-[var(--brand-primary)] underline">
                    + Add Bullet
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button type="button" onClick={addSection} data-testid="admin-legal-section-add" className="btn-secondary mt-3">
            <Plus size={14} /> Add Section
          </button>
        </div>

        {error && <p data-testid="admin-legal-error" className="text-red-400 text-sm">{error}</p>}

        <button type="submit" disabled={saving} data-testid="admin-legal-save-button" className="btn-primary disabled:opacity-50">
          <Save size={14} /> {saving ? "Saving…" : "Save Page"}
        </button>
      </form>
    </div>
  );
}
